import { Box } from '@chakra-ui/react';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';

import { Task, TasksFilter } from '@app/api/tasks';
import { QueryTable, QueryTableDataResponse, QueryTableParams } from '@app/components/common/table';
import { isGroupRow } from '@app/components/common/table/utils';
import { useApi } from '@app/hooks';

import { useColumns } from './columns';
import { CreateImportTaskDialog } from './components/create-import-task-dialog';
import { TaskDetailsDialog } from './components/details-dialog';
import { TASKS_ROUTE } from './constants';

const toFilterValue = (value?: string | string[] | number[]) =>
  (Array.isArray(value) ? value[0] : value) || undefined;

const mapFilter = (filterRaw: QueryTableParams<Task>['filterRaw'] = {}): TasksFilter => ({
  type: toFilterValue(filterRaw.type) as TasksFilter['type'],
  status: toFilterValue(filterRaw.status) as TasksFilter['status'],
});

const parseTaskId = (value?: string): number | null => {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  return Number(value);
};

/** Пробрасывает refetch таблицы наружу (контекст доступен только внутри QueryTable). */
const RefetchBridge = ({ refetchRef }: { refetchRef: MutableRefObject<(() => void) | null> }) => {
  const { refetch } = QueryTable.useQueryTable();
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch, refetchRef]);
  return null;
};

export const TasksTable = () => {
  const api = useApi();
  const history = useHistory();
  const params = useParams<{ id?: string }>();
  const columns = useColumns();
  const [showCreate, setShowCreate] = useState(false);
  const refetchRef = useRef<(() => void) | null>(null);

  // id задачи живёт в адресе: /tools/tasks/:id открывает карточку задачи.
  const selectedId = useMemo(() => parseTaskId(params.id), [params.id]);

  // Фильтры и пагинация таблицы хранятся в query-строке — сохраняем её при смене задачи.
  const openTask = useCallback(
    (id: number | null) => {
      const search = window.location.search;
      history.push(id != null ? `${TASKS_ROUTE}/${id}${search}` : `${TASKS_ROUTE}${search}`);
    },
    [history]
  );

  const fetchTasks = useCallback(
    async (queryParams: QueryTableParams<Task>): Promise<QueryTableDataResponse<Task>> => {
      try {
        const response = await api.tasks.getTasks({
          limit: queryParams.pagination.pageSize,
          offset: (queryParams.pagination.page - 1) * queryParams.pagination.pageSize,
          filter: mapFilter(queryParams.filterRaw),
        });

        return {
          code: response?.code,
          data: response?.data?.data || [],
          total: response?.data?.total || 0,
        };
      } catch (e) {
        console.error(e);
        return { data: [], total: 0 };
      }
    },
    [api.tasks]
  );

  const onRowClick = useCallback(
    (rowData: Task | { items?: Task[] }) => {
      if (isGroupRow(rowData)) {
        return;
      }
      openTask((rowData as Task).id);
    },
    [openTask]
  );

  const refetch = useCallback(() => {
    refetchRef.current?.();
  }, []);

  const onCreated = useCallback(
    (taskId: number) => {
      refetch();
      openTask(taskId);
    },
    [refetch, openTask]
  );

  return (
    <Box
      sx={{
        'table tbody tr:hover': {
          bg: 'gray.100 !important',
        },
      }}
    >
      <QueryTable<Task>
        title="Фоновые задачи"
        fetchData={fetchTasks}
        columns={columns}
        keyProperty="id"
        onRowClick={onRowClick}
        selectedRow={selectedId != null ? ({ id: selectedId } as Task) : undefined}
        onCreateClick={() => setShowCreate(true)}
      >
        <RefetchBridge refetchRef={refetchRef} />
      </QueryTable>
      <CreateImportTaskDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={onCreated} />
      <TaskDetailsDialog taskId={selectedId} onClose={() => openTask(null)} onChanged={refetch} />
    </Box>
  );
};
